import { token } from "@atlaskit/tokens";
import { MultiSelectOption } from "@shared/ui/MultiSelectInput";
import { createStaticRanges, DateRange } from "react-date-range";

export type NewReportDialogProps = {
    isOpen: boolean;
    onClose: () => void;
    report?: {
        reportId: string;
        reportName: string;
        dpId: string;
        spId: string;
        dimensions: string;
        metrics: string;
        appId: string;
        adUnitType: string;
        country: string;
        platform: string;
        startDate: string;
        endDate: string;
    } | null;
};

export type ReportDateRangeFieldProps = {
    endDate: string;
    isHourly: boolean;
    onChange: (range: { startDate: string; endDate: string }) => void;
    startDate: string;
};

export type ReportFormState = {
    reportName: string;
    startDate: string;
    endDate: string;
    isHourly: boolean;
    dimensions: MultiSelectOption[];
    metrics: MultiSelectOption[];
    appId: MultiSelectOption[];
    country: MultiSelectOption[];
    adUnitType: MultiSelectOption[];
    platform: MultiSelectOption[];
    spId: MultiSelectOption[];
    dpId: MultiSelectOption[];
};

export const metricOptions = [

    {
        value: "total_requests",
        label: "Total requests",
    },
    // {
    //     value: "match_rate",
    //     label: "Match Rate",
    // },
    {
        value: "win_bids",
        label: "Win bids",
    },
    {
        value: "out_bids",
        label: "Out bids",
    },
    {
        value: "no_bids",
        label: "No bids",
    },
    {
        value: "timeouts",
        label: "Timeouts",
    },
    {
        value: "impressions",
        label: "Impressions",
    },
    {
        value: "clicks",
        label: "Clicks",
    },
    {
        value: "ecpm",
        label: "ECPM",
    },
    {
        value: "revenue",
        label: "Revenue",
    },
]



export const dialogOverlayStyles = {
    alignItems: 'center',
    background: 'var(--app-overlay)',
    bottom: 0,
    display: 'flex',
    justifyContent: 'center',
    left: 0,
    overflowY: 'auto',
    padding: token('space.400'),
    position: 'fixed',
    right: 0,
    top: 0,
    zIndex: 1100
} as const;

export const dialogCardStyles = {
    background: token('elevation.surface.overlay'),
    borderRadius: '24px',
    boxShadow: token('elevation.shadow.overlay'),
    maxHeight: '100%',
    maxWidth: '1280px',
    overflowY: 'auto',
    padding: '32px 40px 40px',
    width: '100%'
} as const;

export const platformOptions = [
    {
        value: "Android",
        label: "ANDROID",
    },
    {
        value: "iOS",
        label: "IOS",
    },
];

export const fieldSectionStyles = {
    background: token('color.background.neutral.subtle'),
    border: `1px solid ${token('color.border')}`,
    borderRadius: '20px',
    padding: '20px'
} as const;

export const footerButtonStyles = {
    borderRadius: '16px',
    minWidth: '160px'
} as const;

export const confirmButtonStyles = {
    borderRadius: '12px',
    minWidth: '120px'
} as const;

export const dateTriggerStyles = {
    alignItems: 'center',
    background: token('elevation.surface.overlay'),
    border: `1px solid ${token('color.border')}`,
    borderRadius: '16px',
    color: token('color.text'),
    cursor: 'pointer',
    display: 'inline-flex',
    gap: '12px',
    justifyContent: 'space-between',
    minHeight: '52px',
    padding: '0 16px',
    width: '100%'
} as const;

export const dateTriggerChipStyles = {
    background: token('color.background.discovery'),
    borderRadius: '999px',
    color: token('color.text.brand'),
    fontSize: '0.875rem',
    fontWeight: 600,
    padding: '6px 10px'
} as const;

export const dialogTitleStyles = {
    color: token('color.text'),
    fontSize: '1.75rem',
    fontWeight: 700,
    lineHeight: 1.1,
    margin: 0
} as const;

export const sectionHeadingStyles = {
    color: token('color.text'),
    fontSize: '1.5rem',
    fontWeight: 600,
    margin: 0
} as const;

export const dimensionOptions: MultiSelectOption[] = [
    { label: 'App Name', value: 'app_id' },
    { label: 'Country', value: 'country' },
    { label: 'Ad Unit Type', value: 'ad_unit_type' },
    { label: 'Platform', value: 'platform' },
    { label: 'SP ID', value: 'sp_id' },
    { label: 'DP ID', value: 'dp_id' }
];


export const shiftDate = (date: Date, offsetDays: number) => {
    const nextDate = new Date(date);
    nextDate.setDate(nextDate.getDate() + offsetDays);
    return nextDate;
};

export const formatDateInput = (date: Date) => {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
};

export const formatDateLabel = (dateValue: string) => {
    const date = new Date(`${dateValue}T00:00:00`);
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });
};

export const parseDateInput = (dateValue: string) => new Date(`${dateValue}T00:00:00`);

export const buildSelection = (startDate: string, endDate: string): DateRange => ({
    startDate: parseDateInput(startDate),
    endDate: parseDateInput(endDate),
    key: 'selection'
});

export const resolveDatePresetLabel = (startDate: string, endDate: string) => {
    const today = formatDateInput(new Date());
    const yesterday = formatDateInput(shiftDate(new Date(), -1));

    if (startDate === today && endDate === today) {
        return 'Today';
    }

    if (startDate === yesterday && endDate === yesterday) {
        return 'Yesterday';
    }

    if (startDate === formatDateInput(shiftDate(new Date(), -7)) && endDate === yesterday) {
        return 'Last week';
    }

    if (startDate === formatDateInput(shiftDate(new Date(), -30)) && endDate === yesterday) {
        return 'Last 30 days';
    }

    if (startDate === formatDateInput(shiftDate(new Date(), -90)) && endDate === yesterday) {
        return 'Last 90 days';
    }

    return 'Custom';
};

export const reportDateStaticRanges = createStaticRanges([
    {
        label: 'Today',
        range: () => {
            const today = new Date();
            return { startDate: today, endDate: today };
        }
    },
    {
        label: 'Yesterday',
        range: () => {
            const yesterday = shiftDate(new Date(), -1);
            return { startDate: yesterday, endDate: yesterday };
        }
    },
    {
        label: 'Last 7 days',
        range: () => {
            const today = new Date();
            const yesterday = shiftDate(today, -1);
            return { startDate: shiftDate(today, -7), endDate: yesterday };
        }
    },
    {
        label: 'Last 30 days',
        range: () => {
            const today = new Date();
            const yesterday = shiftDate(today, -1);
            return { startDate: shiftDate(today, -30), endDate: yesterday };
        }
    },
    {
        label: 'Last 90 days',
        range: () => {
            const today = new Date();
            const yesterday = shiftDate(today, -1);
            return { startDate: shiftDate(today, -90), endDate: yesterday };
        }
    }
]);

export const createInitialFormState = (): ReportFormState => {
    const today = new Date();
    const yesterday = shiftDate(today, -1);

    return {
        reportName: '',
        startDate: formatDateInput(shiftDate(today, -7)),
        endDate: formatDateInput(yesterday),
        isHourly: true,
        dimensions: [],
        metrics: [],
        appId: [],
        country: [],
        adUnitType: [],
        platform: [],
        spId: [],
        dpId: []
    };
};
